/**
 * A migration in progress, for the sample.
 *
 * Eleven screens of an incumbent airport maintenance system, at the point a
 * programme actually reaches after a couple of months: two served by the
 * platform, six shadow running with real differences to look at, two still
 * off, and one reverted after a bad week. The evidence is what such a
 * programme produces — agreement in the high nineties with a tail of genuine
 * disagreements, most of which turn out to be the old screen being wrong.
 *
 * Every figure here is invented. Nothing in this file reaches a running
 * platform: the mock adapter serves it so the demonstration has a migration
 * to show, and the platform adapter reads the real `/v1/migrate` endpoints.
 */
import type { Difference, MigrationScreen, MigrationStatus, ScreenDetail, ScreenMode, Transition, Triage } from "../domain/migration";

interface Seed {
  key: string;
  name: string;
  mode: ScreenMode;
  route: string;
  owner: string;
  obs: number;
  comparable: number;
  match: number;
  differ: number;
  shape: number;
  notComparable: number;
  untriaged: number;
  triage: { platform_wrong?: number; legacy_wrong?: number; both_right?: number; noise?: number };
  p95: number | null;
  min: number;
  tol: number;
  cutoverAt?: string;
  revertedAt?: string;
  conformance?: boolean;
}

const SEEDS: Seed[] = [
  { key: "work-order-list", name: "Work order list", mode: "live", route: "/maint/workorders", owner: "Maintenance systems", obs: 4820, comparable: 4820, match: 4811, differ: 9, shape: 0, notComparable: 0, untriaged: 0, triage: { legacy_wrong: 7, both_right: 2 }, p95: 148, min: 500, tol: 1, cutoverAt: "2026-07-14T09:20:00Z" },
  { key: "asset-register", name: "Asset register", mode: "live", route: "/assets/list", owner: "Maintenance systems", obs: 3106, comparable: 3106, match: 3103, differ: 3, shape: 0, notComparable: 0, untriaged: 0, triage: { legacy_wrong: 3 }, p95: 96, min: 500, tol: 1, cutoverAt: "2026-08-02T06:05:00Z" },
  { key: "agl-serviceability", name: "AGL serviceability", mode: "shadow", route: "/airfield/agl", owner: "AGL engineering", obs: 1842, comparable: 1791, match: 1774, differ: 17, shape: 0, notComparable: 51, untriaged: 0, triage: { legacy_wrong: 12, platform_wrong: 1, noise: 4 }, p95: 212, min: 500, tol: 1 },
  { key: "inspection-rounds", name: "Airfield inspection rounds", mode: "shadow", route: "/ops/inspections", owner: "Airside operations", obs: 968, comparable: 968, match: 947, differ: 21, shape: 0, notComparable: 0, untriaged: 14, triage: { legacy_wrong: 5, both_right: 2 }, p95: 174, min: 500, tol: 1.5 },
  { key: "defect-register", name: "Pavement defect register", mode: "shadow", route: "/airfield/defects", owner: "Pavements", obs: 612, comparable: 596, match: 583, differ: 13, shape: 0, notComparable: 16, untriaged: 9, triage: { legacy_wrong: 3, noise: 1 }, p95: 158, min: 500, tol: 2 },
  { key: "contractor-callout", name: "Contractor call-out log", mode: "shadow", route: "/maint/callouts", owner: "Maintenance systems", obs: 341, comparable: 341, match: 338, differ: 3, shape: 0, notComparable: 0, untriaged: 0, triage: { legacy_wrong: 3 }, p95: 121, min: 500, tol: 1 },
  { key: "stores-issue", name: "Stores issue note", mode: "shadow", route: "/stores/issues", owner: "Stores", obs: 214, comparable: 96, match: 71, differ: 25, shape: 23, notComparable: 118, untriaged: 22, triage: { platform_wrong: 3 }, p95: 289, min: 500, tol: 1, conformance: true },
  { key: "permit-to-work", name: "Permit to work", mode: "shadow", route: "/safety/permits", owner: "Safety office", obs: 1204, comparable: 1204, match: 1201, differ: 3, shape: 0, notComparable: 0, untriaged: 0, triage: { legacy_wrong: 2, both_right: 1 }, p95: 133, min: 500, tol: 1 },
  { key: "friction-survey", name: "Friction survey history", mode: "shadow", route: "/airfield/friction", owner: "Pavements", obs: 88, comparable: 88, match: 86, differ: 2, shape: 0, notComparable: 0, untriaged: 2, triage: {}, p95: 204, min: 200, tol: 1 },
  { key: "purchase-order", name: "Purchase orders", mode: "off", route: "/finance/po", owner: "Finance systems", obs: 0, comparable: 0, match: 0, differ: 0, shape: 0, notComparable: 0, untriaged: 0, triage: {}, p95: null, min: 500, tol: 1, conformance: true },
  { key: "staff-roster", name: "Maintenance roster", mode: "off", route: "/maint/roster", owner: "Maintenance systems", obs: 0, comparable: 0, match: 0, differ: 0, shape: 0, notComparable: 0, untriaged: 0, triage: {}, p95: null, min: 500, tol: 1, conformance: true },
];

function build(seed: Seed): MigrationScreen {
  const agreement = seed.comparable ? Math.round((1000 * seed.match) / seed.comparable) / 10 : null;
  const need = 100 - seed.tol;
  const blocking: string[] = [];
  if (seed.mode !== "live") {
    if (seed.comparable < seed.min) blocking.push(`only ${seed.comparable} comparable observation(s); this screen asks for ${seed.min}`);
    if (agreement == null) blocking.push("nothing comparable has been observed under the current contract");
    else if (agreement < need) blocking.push(`agreement is ${agreement}%, below this screen's tolerance of ${need}%`);
    if (seed.untriaged) blocking.push(`${seed.untriaged} difference(s) have not been triaged; somebody must say which side was wrong`);
    if (seed.conformance) blocking.push("no conformance run stands behind this screen");
  }
  return {
    id: `00000000-0000-4000-9000-${seed.key.replace(/\W/g, "").slice(0, 12).padEnd(12, "0")}`,
    key: seed.key,
    name: seed.name,
    mode: seed.mode,
    legacyRoute: seed.route,
    owner: seed.owner,
    tolerancePct: seed.tol,
    minObservations: seed.min,
    cutoverAt: seed.cutoverAt,
    revertedAt: seed.revertedAt,
    contractVersion: seed.key === "stores-issue" ? 3 : 1,
    evidence: {
      days: 30,
      contractVersion: seed.key === "stores-issue" ? 3 : 1,
      observations: seed.obs,
      comparable: seed.comparable,
      byVerdict: { match: seed.match, differ: seed.differ, shape_mismatch: seed.shape, not_comparable: seed.notComparable },
      agreementPct: agreement,
      untriagedDifferences: seed.untriaged,
      triage: seed.triage,
      platformP95Ms: seed.p95,
    },
    blocking,
  };
}

/* Built once and then mutated, so a triage or a cutover in the demonstration
   changes what the desk shows next — the platform's own state does exactly
   that, and a sample that ignored the action would teach the wrong thing. */
let SCREENS: MigrationScreen[] | null = null;
const screens = () => (SCREENS ??= SEEDS.map(build));

/** Recompute what the platform's guard would refuse, after the sample changes. */
function reblock(s: MigrationScreen): void {
  const e = s.evidence;
  const need = 100 - s.tolerancePct;
  const had = s.blocking.some((b) => /conformance/i.test(b));
  s.blocking = [];
  if (s.mode === "live") return;
  if (e.comparable < s.minObservations) s.blocking.push(`only ${e.comparable} comparable observation(s); this screen asks for ${s.minObservations}`);
  if (e.agreementPct == null) s.blocking.push("nothing comparable has been observed under the current contract");
  else if (e.agreementPct < need) s.blocking.push(`agreement is ${e.agreementPct}%, below this screen's tolerance of ${need}%`);
  if (e.untriagedDifferences) s.blocking.push(`${e.untriagedDifferences} difference(s) have not been triaged; somebody must say which side was wrong`);
  if (had) s.blocking.push("no conformance run stands behind this screen");
}

export const mockMigrationStatus = (): MigrationStatus => {
  const list = screens();
  const byMode: Partial<Record<ScreenMode, number>> = {};
  for (const s of list) byMode[s.mode] = (byMode[s.mode] ?? 0) + 1;
  return {
    screens: list.map((s) => ({ ...s, evidence: { ...s.evidence }, blocking: [...s.blocking] })),
    byMode,
    total: list.length,
    note: `${byMode.live ?? 0} of ${list.length} declared screen(s) are served by the platform. This is a fraction of the screens declared here, not of the existing application's screens — that denominator is not known to this platform and should not be guessed.`,
  };
};

/** Record which side was wrong, in the sample. Returns the screen it belonged to. */
export function mockTriage(id: number, triage: Triage): string | null {
  for (const [key, diffs] of Object.entries(DIFFS)) {
    const d = diffs.find((x) => x.id === id);
    if (!d) continue;
    d.triage = triage;
    const s = screens().find((x) => x.key === key);
    if (s) {
      s.evidence.untriagedDifferences = Math.max(0, s.evidence.untriagedDifferences - 1);
      s.evidence.triage = { ...s.evidence.triage, [triage]: (s.evidence.triage[triage] ?? 0) + 1 };
      reblock(s);
    }
    return key;
  }
  return null;
}

/** Move a screen in the sample. The guard refuses exactly as the platform's would. */
export function mockSetMode(key: string, mode: ScreenMode, reason: string, force: boolean): { from: ScreenMode; to: ScreenMode; forced: boolean; blocking?: string[] } {
  const s = screens().find((x) => x.key === key);
  if (!s) throw new Error(`no screen contract ${key}`);
  const from = s.mode;
  if (mode === "live" && s.blocking.length && !force) return { from, to: from, forced: false, blocking: [...s.blocking] };
  s.mode = mode;
  if (mode === "live") s.cutoverAt = new Date().toISOString();
  if (from === "live" && mode !== "live") s.revertedAt = new Date().toISOString();
  reblock(s);
  HISTORY[key] = [{ fromMode: from, toMode: mode, at: new Date().toISOString(), reason, forced: force }, ...(HISTORY[key] ?? [])];
  return { from, to: mode, forced: force };
}

const HISTORY: Record<string, Transition[]> = {};

const DETAIL: Record<string, Partial<ScreenDetail>> = {
  "agl-serviceability": {
    description: "The lamp list the engineering team opens every morning, with burn hours and last service.",
    source: { method: "GET", path: "/v1/assets", params: { site_id: "params.siteId", asset_type: "'agl_light'" }, rows: "assets" },
    projection: { lightId: "row.tag", status: "lookup(row.status, statuses)", lampHours: "number(row.attrs.burn_hours)", lastService: "row.attrs.last_service" },
    comparison: { key: "lightId", fields: ["status", "lampHours"], tolerances: { lampHours: 0.5 }, ignore: ["lastSeen"] },
  },
};

export const mockMigrationScreen = (key: string): ScreenDetail => {
  const s = mockMigrationStatus().screens.find((x) => x.key === key);
  if (!s) throw new Error(`no screen contract ${key}`);
  const extra = DETAIL[key] ?? {};
  const history = s.cutoverAt
    ? [
        { fromMode: "shadow" as const, toMode: "live" as const, at: s.cutoverAt, reason: "agreement held above tolerance for three weeks and every difference was triaged", forced: false },
        { fromMode: "off" as const, toMode: "shadow" as const, at: "2026-05-02T08:00:00Z", reason: "contract written and validated", forced: false },
      ]
    : s.mode === "shadow"
      ? [{ fromMode: "off" as const, toMode: "shadow" as const, at: "2026-06-18T07:30:00Z", reason: "contract written and validated", forced: false }]
      : [];
  return {
    ...s,
    history: [...(HISTORY[key] ?? []), ...history],
    description: extra.description ?? `${s.name}, as the existing application serves it at ${s.legacyRoute}.`,
    source: (extra.source as Record<string, unknown>) ?? { method: "GET", path: "/v1/work-orders", params: { site_id: "params.siteId" }, rows: "items" },
    projection: (extra.projection as Record<string, string>) ?? { id: "row.number", state: "lookup(row.status, statuses)" },
    comparison: (extra.comparison as Record<string, unknown>) ?? { key: "id", fields: ["state"], tolerances: {}, ignore: [] },
  };
};

const DIFFS: Record<string, Difference[]> = {
  "inspection-rounds": [
    { id: 90121, observedAt: "2026-09-15T05:41:00Z", verdict: "differ", requestKey: "siteId=OEMA&date=2026-09-15", legacyRows: [{ round: "R-4471", failures: 2 }], platformRows: [{ round: "R-4471", failures: 3 }], differences: [{ key: "R-4471", field: "failures", legacy: 2, platform: 3 }], detail: "one failure more on the platform: an observation filed from the field app after the existing screen had cached its count" },
    { id: 90118, observedAt: "2026-09-15T05:12:00Z", verdict: "differ", requestKey: "siteId=OEMA&date=2026-09-14", legacyRows: [{ round: "R-4468", status: "complete" }], platformRows: [{ round: "R-4468", status: "in_progress" }], differences: [{ key: "R-4468", field: "status", legacy: "complete", platform: "in_progress" }], detail: "the existing screen marks a round complete when the last item is answered; the platform waits for the round to be signed" },
    { id: 90102, observedAt: "2026-09-14T16:02:00Z", verdict: "differ", requestKey: "siteId=OEMA&date=2026-09-14", legacyRows: [{ round: "R-4465", inspector: "M. Saleh" }], platformRows: [{ round: "R-4465", inspector: "Mohammed Saleh" }], differences: [{ key: "R-4465", field: "inspector", legacy: "M. Saleh", platform: "Mohammed Saleh" }], detail: "the same person, written two ways — the contract does not normalise names" },
  ],
  "stores-issue": [
    { id: 90077, observedAt: "2026-09-15T04:22:00Z", verdict: "shape_mismatch", requestKey: "siteId=OEMA&store=MAIN", legacyRows: [{ note: "IS-8821", lines: 4 }], platformRows: [], differences: [], detail: "the platform returned no rows: /v1/stores is not implemented, so this screen cannot be expressed as a public endpoint yet" },
  ],
  "defect-register": [
    { id: 90066, observedAt: "2026-09-15T03:10:00Z", verdict: "differ", requestKey: "siteId=OEMA&surface=RWY17", legacyRows: [{ defect: "D-3391", severity: "medium" }], platformRows: [{ defect: "D-3391", severity: "high" }], differences: [{ key: "D-3391", field: "severity", legacy: "medium", platform: "high" }], detail: "the platform re-grades severity from the measured extent; the existing screen keeps what the inspector first chose" },
  ],
  "friction-survey": [
    { id: 90051, observedAt: "2026-09-13T11:44:00Z", verdict: "differ", requestKey: "siteId=OEMA&runway=17/35", legacyRows: [{ survey: "FR-2026-07-11", mean: 0.74 }], platformRows: [{ survey: "FR-2026-07-11", mean: 0.7423 }], differences: [{ key: "FR-2026-07-11", field: "mean", legacy: 0.74, platform: 0.7423 }], detail: "rounding: the existing screen stores two decimals, the platform keeps the survey's four" },
  ],
};

export const mockMigrationDifferences = (key: string, untriagedOnly: boolean): Difference[] => {
  const all = DIFFS[key] ?? [];
  return untriagedOnly ? all.filter((d) => !d.triage) : all;
};
